import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Bulk Payout Creator - In-app table editor with saved beneficiary selection.
 * Allows users to create multiple payouts at once by:
 * 1. Adding rows from saved beneficiaries
 * 2. Manually entering new beneficiary details
 * 3. Pasting from clipboard
 */
const BulkPayoutCreator = ({ onClose, onSuccess, payoutToken }) => {
  const [rows, setRows] = useState([createEmptyRow()]);
  const [savedBeneficiaries, setSavedBeneficiaries] = useState([]);
  const [showBeneficiaryPicker, setShowBeneficiaryPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const fetchBeneficiaries = async () => {
    try {
      const res = await axios.get('/api/v1/payouts/beneficiaries', {
        headers: payoutToken ? { Authorization: `Bearer ${payoutToken}` } : {}
      });
      setSavedBeneficiaries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to fetch beneficiaries:', e);
    }
  };

  function createEmptyRow() {
    return {
      id: Date.now() + Math.random(),
      idempotencyKey: `${Date.now()}-${Math.random().toString(36).slice(2)}`,  // generated once per row, stable on retry
      accountNumber: '',
      ifscCode: '',
      beneficiaryName: '',
      amount: '',
      remarks: '',
      saveForFuture: false
    };
  }

  const addRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
    // Clear error for this field
    const errKey = `${index}-${field}`;
    if (errors[errKey]) {
      const newErrors = { ...errors };
      delete newErrors[errKey];
      setErrors(newErrors);
    }
  };

  const addFromSaved = (beneficiaries) => {
    const existingAccounts = rows.map(r => r.accountNumber).filter(Boolean);
    const newBeneficiaries = beneficiaries.filter(b => !existingAccounts.includes(b.accountNumber));
    
    if (newBeneficiaries.length < beneficiaries.length) {
      const skipped = beneficiaries.length - newBeneficiaries.length;
      alert(`${skipped} beneficiary(s) already in the table — skipped.`);
    }

    const newRows = newBeneficiaries.map(b => ({
      id: Date.now() + Math.random(),
      idempotencyKey: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      accountNumber: b.accountNumber || '',
      ifscCode: b.ifscCode || '',
      beneficiaryName: b.name || '',
      amount: '',
      remarks: '',
      saveForFuture: false
    }));
    setRows([...rows.filter(r => r.accountNumber || r.beneficiaryName), ...newRows]);
    setShowBeneficiaryPicker(false);
  };

  const validateRows = () => {
    const newErrors = {};
    let valid = true;

    rows.forEach((row, i) => {
      if (!row.accountNumber || !row.accountNumber.match(/^\d{7,18}$/)) {
        newErrors[`${i}-accountNumber`] = 'Invalid (7-18 digits)';
        valid = false;
      }
      if (!row.ifscCode || !row.ifscCode.match(/^[A-Z]{4}0[A-Z0-9]{6}$/)) {
        newErrors[`${i}-ifscCode`] = 'Invalid IFSC format';
        valid = false;
      }
      if (!row.beneficiaryName || row.beneficiaryName.trim().length < 2) {
        newErrors[`${i}-beneficiaryName`] = 'Name required';
        valid = false;
      }
      if (!row.amount || parseFloat(row.amount) < 1 || parseFloat(row.amount) > 500000) {
        newErrors[`${i}-amount`] = '₹1 - ₹5,00,000';
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateRows()) return;

    setSubmitting(true);
    setSubmitResult(null);

    const requests = rows.map(row => ({
      paymentMode: 'IMPS',
      beneficiaryAccount: row.accountNumber,
      ifscCode: row.ifscCode,
      beneficiaryName: row.beneficiaryName,
      amount: parseFloat(row.amount),
      payoutType: 'CASHBACK',
      remarks: row.remarks || 'Bulk Payout',
      saveBeneficiary: row.saveForFuture,
      idempotencyKey: row.idempotencyKey  // stable per row, reused on retry
    }));

    try {
      const res = await axios.post('/api/v1/payouts/bulk-create', requests, {
        headers: payoutToken ? { Authorization: `Bearer ${payoutToken}` } : {}
      });
      setSubmitResult(res.data);
      if (res.data.successCount > 0 && onSuccess) {
        onSuccess(res.data);
        // Auto-close after 2 seconds on success
        setTimeout(() => onClose(), 2000);
      }
    } catch (e) {
      setSubmitResult({ error: e.response?.data?.message || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaste = useCallback((e) => {
    const clipboardData = e.clipboardData.getData('text');
    if (!clipboardData || !clipboardData.includes('\t') && !clipboardData.includes(',')) return;

    e.preventDefault();
    const delimiter = clipboardData.includes('\t') ? '\t' : ',';
    const lines = clipboardData.trim().split('\n').filter(l => l.trim());
    
    const newRows = lines.map(line => {
      const cols = line.split(delimiter).map(c => c.trim());
      return {
        id: Date.now() + Math.random(),
        idempotencyKey: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        accountNumber: cols[0] || '',
        ifscCode: cols[1] || '',
        beneficiaryName: cols[2] || '',
        amount: cols[3] || '',
        remarks: cols[4] || '',
        saveForFuture: false
      };
    });

    if (newRows.length > 0) {
      setRows(prev => [...prev.filter(r => r.accountNumber || r.beneficiaryName), ...newRows]);
    }
  }, []);

  const totalAmount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const validRowCount = rows.filter(r => r.accountNumber && r.ifscCode && r.beneficiaryName && r.amount).length;

  const filteredBeneficiaries = savedBeneficiaries.filter(b =>
    (b.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.accountNumber || '').includes(searchTerm)
  );


  return (
    <div className="modal-overlay" role="presentation" onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div className="modal-content payout-modal" style={{ maxWidth: '1100px', maxHeight: '90vh', overflow: 'auto' }} role="dialog" aria-modal="true">
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Create Payout</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Action Bar */}
        <div style={{ padding: '12px 20px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setShowBeneficiaryPicker(true)} style={btnStyle('#1976d2')}>
            + Add from Saved Beneficiaries
          </button>
          <button onClick={addRow} style={btnStyle('#388e3c')}>
            + Add Empty Row
          </button>
          <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#555' }}>
            {validRowCount} valid of {rows.length} rows | Total: ₹{totalAmount.toLocaleString('en-IN')}
          </span>
          <span style={{ fontSize: '12px', color: '#888' }}>
            Tip: Paste from Excel (Account, IFSC, Name, Amount, Remarks)
          </span>
        </div>

        {/* Table */}
        <div style={{ padding: '12px 20px', overflowX: 'auto' }} onPaste={handlePaste}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Account Number *</th>
                <th style={thStyle}>IFSC *</th>
                <th style={thStyle}>Beneficiary Name *</th>
                <th style={thStyle}>Amount (₹) *</th>
                <th style={thStyle}>Remarks</th>
                <th style={thStyle}>Save</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={row.accountNumber}
                      onChange={e => updateRow(i, 'accountNumber', e.target.value)}
                      placeholder="e.g. 000451000302"
                      style={inputStyle(errors[`${i}-accountNumber`])}
                      aria-label={`Row ${i+1} account number`}
                    />
                    {errors[`${i}-accountNumber`] && <div style={errStyle}>{errors[`${i}-accountNumber`]}</div>}
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={row.ifscCode}
                      onChange={e => updateRow(i, 'ifscCode', e.target.value.toUpperCase())}
                      placeholder="e.g. ICIC0000011"
                      style={inputStyle(errors[`${i}-ifscCode`])}
                      maxLength={11}
                      aria-label={`Row ${i+1} IFSC code`}
                    />
                    {errors[`${i}-ifscCode`] && <div style={errStyle}>{errors[`${i}-ifscCode`]}</div>}
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={row.beneficiaryName}
                      onChange={e => updateRow(i, 'beneficiaryName', e.target.value)}
                      placeholder="Name"
                      style={inputStyle(errors[`${i}-beneficiaryName`])}
                      aria-label={`Row ${i+1} beneficiary name`}
                    />
                    {errors[`${i}-beneficiaryName`] && <div style={errStyle}>{errors[`${i}-beneficiaryName`]}</div>}
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      value={row.amount}
                      onChange={e => updateRow(i, 'amount', e.target.value)}
                      placeholder="0.00"
                      min="1"
                      max="500000"
                      style={{ ...inputStyle(errors[`${i}-amount`]), width: '100px' }}
                      aria-label={`Row ${i+1} amount`}
                    />
                    {errors[`${i}-amount`] && <div style={errStyle}>{errors[`${i}-amount`]}</div>}
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={row.remarks}
                      onChange={e => updateRow(i, 'remarks', e.target.value)}
                      placeholder="Optional"
                      style={{ ...inputStyle(), width: '120px' }}
                      aria-label={`Row ${i+1} remarks`}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={row.saveForFuture}
                      onChange={e => updateRow(i, 'saveForFuture', e.target.checked)}
                      title="Save this beneficiary for future payouts"
                      aria-label={`Save row ${i+1} beneficiary`}
                    />
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '18px' }} aria-label={`Remove row ${i+1}`}>
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Submit Result */}
        {submitResult && (
          <div style={{ padding: '12px 20px' }}>
            {submitResult.error ? (
              <div style={{ padding: '10px', background: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
                Error: {submitResult.error}
              </div>
            ) : (
              <div style={{ padding: '10px', background: '#e8f5e9', borderRadius: '4px' }}>
                <strong>Batch Complete:</strong> {submitResult.successCount} created, {submitResult.failedCount} failed out of {submitResult.totalRequested}
                {submitResult.failedCount > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    {submitResult.results.filter(r => !r.success).map((r, i) => (
                      <div key={i} style={{ color: '#c62828' }}>Row {r.index + 1}: {r.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#666' }}>
            All payouts will be created as "Pending Approval"
          </span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={btnStyle('#757575')} disabled={submitting}>Cancel</button>
            <button onClick={handleSubmit} style={btnStyle('#1976d2')} disabled={submitting || rows.length === 0}>
              {submitting ? `Creating ${rows.length} payouts...` : `Submit ${validRowCount} Payouts`}
            </button>
          </div>
        </div>

        {/* Beneficiary Picker Modal */}
        {showBeneficiaryPicker && (
          <BeneficiaryPicker
            beneficiaries={filteredBeneficiaries}
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            onSelect={addFromSaved}
            onClose={() => setShowBeneficiaryPicker(false)}
            payoutToken={payoutToken}
            onRefresh={fetchBeneficiaries}
          />
        )}
      </div>
    </div>
  );
};

// Beneficiary Picker Sub-component
const BeneficiaryPicker = ({ beneficiaries, searchTerm, onSearch, onSelect, onClose, payoutToken, onRefresh }) => {
  const [selected, setSelected] = useState([]);

  const toggle = (b) => {
    setSelected(prev =>
      prev.find(s => s.id === b.id)
        ? prev.filter(s => s.id !== b.id)
        : [...prev, b]
    );
  };

  const removeBeneficiary = async (id) => {
    if (!window.confirm('Remove this beneficiary from saved list?')) return;
    try {
      await axios.delete(`/api/v1/payouts/beneficiaries/${id}`, {
        headers: payoutToken ? { Authorization: `Bearer ${payoutToken}` } : {}
      });
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Failed to remove: ' + (e.response?.data?.message || e.message));
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '8px', width: '500px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h4 style={{ margin: 0 }}>Select Saved Beneficiaries</h4>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>&times;</button>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search by name or account..."
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            aria-label="Search beneficiaries"
          />
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {beneficiaries.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>No saved beneficiaries found</p>
          ) : (
            beneficiaries.map(b => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', padding: '8px', borderBottom: '1px solid #f0f0f0', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={!!selected.find(s => s.id === b.id)}
                  onChange={() => toggle(b)}
                />
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => toggle(b)}>
                  <div style={{ fontWeight: '600', fontSize: '13px' }}>{b.name || 'Unknown'}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {b.accountNumber ? `A/C: ****${b.accountNumber.slice(-4)}` : ''} 
                    {b.ifscCode ? ` | ${b.ifscCode}` : ''}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeBeneficiary(b.id); }}
                  style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '16px', padding: '4px 8px' }}
                  title="Remove from saved"
                  aria-label={`Remove ${b.name}`}
                >
                  🗑️
                </button>
              </div>
            ))
          )}
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', color: '#555' }}>{selected.length} selected</span>
          <button
            onClick={() => onSelect(selected)}
            disabled={selected.length === 0}
            style={btnStyle(selected.length > 0 ? '#1976d2' : '#bbb')}
          >
            Add {selected.length} to Table
          </button>
        </div>
      </div>
    </div>
  );
};

// Styles
const btnStyle = (bg) => ({
  padding: '8px 16px',
  background: bg,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
});

const thStyle = {
  padding: '8px 6px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
  fontSize: '12px',
  fontWeight: '600',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '4px 6px',
  verticalAlign: 'top'
};

const inputStyle = (hasError) => ({
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${hasError ? '#c62828' : '#ddd'}`,
  borderRadius: '3px',
  fontSize: '13px',
  outline: 'none'
});

const errStyle = {
  fontSize: '10px',
  color: '#c62828',
  marginTop: '2px'
};

export default BulkPayoutCreator;
